import { PageTitleRow } from "../../../components/layout/PageTitleRow";
import { FormSelect } from "../../../components/ui/select";
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import i18n from '../../../i18n';
import { Trans } from 'react-i18next';
import { AppLayout, PageBackLink } from '../../../components/layout';
import '../inventory.css';
import {
  fetchProductById,
  createProduct,
  updateProduct,
  fetchAddProductFormLookups,
  searchPartNumberSuggestions,
} from '../../../services/productCatalogService';
import MetricTreeSelect from '../components/MetricTreeSelect';

const TOAST_KEY = 'product_catalog_toast';

function translateProductStatusLabel(value, fallbackLabel) {
  if (value === 'Active') return i18n.t('status.active');
  if (value === 'Inactive') return i18n.t('status.inactive');
  if (value === 'End of Production') return i18n.t('inventory.endOfProduction');
  return fallbackLabel || value;
}

/** Digits + optional single decimal (prices). */
function sanitizeDecimalInput(raw) {
  const cleaned = String(raw || '').replace(/[^\d.]/g, '');
  const parts = cleaned.split('.');
  if (parts.length <= 1) return cleaned;
  return `${parts[0]}.${parts.slice(1).join('')}`;
}

/** Digits only (qty fields). */
function sanitizeIntegerInput(raw) {
  return String(raw || '').replace(/\D/g, '');
}

/** HSN: digits only, max 8. */
function sanitizeHsnInput(raw) {
  return String(raw || '').replace(/\D/g, '').slice(0, 8);
}

/** Round money amounts to 2 decimal places; empty when inputs are incomplete. */
function formatCalculatedAmount(value) {
  if (value === '' || value === null || value === undefined || Number.isNaN(Number(value))) return '';
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  return String(Math.round(n * 100) / 100);
}

/** Net Price = List Price − (List Price × Discount on List Price / 100) */
function calculateNetPrice(listPriceValue, discountPctValue) {
  if (listPriceValue === '' || listPriceValue === undefined || isNaN(Number(listPriceValue))) return '';
  const list = Number(listPriceValue);
  const discountPct =
    discountPctValue === '' || discountPctValue === undefined || isNaN(Number(discountPctValue))
      ? 0
      : Number(discountPctValue);
  return formatCalculatedAmount(list - (list * discountPct) / 100);
}

/** Discount at Net Price = Net Price − (Net Price × Standard Discount on Net Price / 100) */
function calculateDiscountAtNetPrice(netPriceValue, standardDiscountPctValue) {
  if (netPriceValue === '' || netPriceValue === undefined || isNaN(Number(netPriceValue))) return '';
  const net = Number(netPriceValue);
  const discountPct =
    standardDiscountPctValue === '' ||
    standardDiscountPctValue === undefined ||
    isNaN(Number(standardDiscountPctValue))
      ? 0
      : Number(standardDiscountPctValue);
  return formatCalculatedAmount(net - (net * discountPct) / 100);
}

export default function AddEditProduct() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const isEditRoute = Boolean(productId);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [lookupsError, setLookupsError] = useState(null);
  const [lookupsLoading, setLookupsLoading] = useState(true);

  const [categories, setCategories] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [primaryVendors, setPrimaryVendors] = useState([]);
  const [secondaryVendors, setSecondaryVendors] = useState([]);
  const [abcOptions, setAbcOptions] = useState([]);
  const [metricCategories, setMetricCategories] = useState([]);

  const [name, setName] = useState('');
  const [partNumber, setPartNumber] = useState('');
  const [partSuggestions, setPartSuggestions] = useState([]);
  const [partSuggestOpen, setPartSuggestOpen] = useState(false);
  const [partSuggestLoading, setPartSuggestLoading] = useState(false);
  const partSuggestRef = useRef(null);

  const [category, setCategory] = useState('');
  const [manufacturer, setManufacturer] = useState('');
  const [status, setStatus] = useState('');
  const [metricId, setMetricId] = useState('');
  const [metricCategory, setMetricCategory] = useState('');
  const [metricLabel, setMetricLabel] = useState('');
  const [listPrice, setListPrice] = useState('');
  const [discountOnListPricePct, setDiscountOnListPricePct] = useState('');
  const [netPrice, setNetPrice] = useState('');
  const [standardDiscountOnNetPricePct, setStandardDiscountOnNetPricePct] = useState('');
  const [discountAtNetPrice, setDiscountAtNetPrice] = useState('');
  // Service Safety Stock and Sales Safety Stock: labels stay as-is; boxes start unchecked.
  const [serviceReserved, setServiceReserved] = useState(false);
  const [safetyStock, setSafetyStock] = useState(false);
  const [underWarranty, setUnderWarranty] = useState(false);
  const [moq, setMoq] = useState('');
  const [hsnCode, setHsnCode] = useState('');
  const [preferredVendor1Id, setPreferredVendor1Id] = useState('');
  const [preferredVendor2Id, setPreferredVendor2Id] = useState('');
  const [abcCategory, setAbcCategory] = useState('');
  const [description, setDescription] = useState('');
  const [productServiceId, setProductServiceId] = useState('');

  const [errors, setErrors] = useState({});
  const [validationAlert, setValidationAlert] = useState(null);

  const statusSelectOptions = useMemo(() => {
    const list = [...(statusOptions || [])];
    if (status && !list.some((o) => o.value === status)) {
      list.unshift({ value: status, label: status });
    }
    return list;
  }, [statusOptions, status]);

  // Category (search/service), Status (search/product-status), and other form lookups.
  useEffect(() => {
    let cancelled = false;

    async function loadLookups() {
      setLookupsLoading(true);
      setLookupsError(null);
      try {
        const lookups = await fetchAddProductFormLookups({ size: 100 });
        if (cancelled) return;

        setCategories(lookups.categories || []);
        setStatusOptions(lookups.statusOptions || []);
        setAbcOptions(lookups.abcOptions || []);
        setMetricCategories(lookups.metricCategories || []);
        setPrimaryVendors(lookups.primaryVendors || []);
        setSecondaryVendors(lookups.secondaryVendors || []);

        if (lookups.errors?.length) {
          setLookupsError(lookups.errors.join(' · '));
        }

        // Default status on Add when API returned options.
        if (!isEditRoute) {
          setStatus((prev) => {
            if (prev) return prev;
            const active = lookups.statusOptions?.find((o) => o.value === 'Active');
            return active?.value || lookups.statusOptions?.[0]?.value || '';
          });
        }
      } catch (err) {
        if (!cancelled) {
          setLookupsError(err?.message || i18n.t('inventory.failedToLoadDropdownOptions'));
          setCategories([]);
          setStatusOptions([]);
          setAbcOptions([]);
          setMetricCategories([]);
          setPrimaryVendors([]);
          setSecondaryVendors([]);
        }
      } finally {
        if (!cancelled) setLookupsLoading(false);
      }
    }

    loadLookups();
    return () => {
      cancelled = true;
    };
  }, [isEditRoute]);

  // Part Number → GET /product-catalog/search/part-number?searchTerm=…
  useEffect(() => {
    const term = partNumber.trim();
    if (term.length < 1) {
      setPartSuggestions([]);
      setPartSuggestOpen(false);
      return undefined;
    }

    let cancelled = false;
    const timer = setTimeout(() => {
      setPartSuggestLoading(true);
      searchPartNumberSuggestions(term, { size: 10 })
        .then((list) => {
          if (cancelled) return;
          setPartSuggestions(list || []);
          setPartSuggestOpen((list || []).length > 0);
        })
        .catch(() => {
          if (!cancelled) {
            setPartSuggestions([]);
            setPartSuggestOpen(false);
          }
        })
        .finally(() => {
          if (!cancelled) setPartSuggestLoading(false);
        });
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [partNumber]);

  useEffect(() => {
    function onDocClick(e) {
      if (!partSuggestRef.current?.contains(e.target)) setPartSuggestOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  // Create: unchecked + "Yes" label. Edit: load from product.
  useEffect(() => {
    if (!isEditRoute) {
      setServiceReserved(false);
      setSafetyStock(false);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;

    async function load() {
      setLoading(true);
      setLoadError(null);
      try {
        const product = await fetchProductById(productId);
        if (cancelled) return;
        if (!product) {
          setLoadError(i18n.t('validation.productNotFound'));
          return;
        }
        setName(product.name || '');
        setPartNumber(product.partNumber || '');
        setCategory(product.category || '');
        setManufacturer(product.manufacturer || '');
        setStatus(product.status === 'Inactive' ? 'Active' : product.status || '');
        setMetricId(product.metricId || '');
        setMetricCategory(product.metricCategory || '');
        setMetricLabel(product.unitOfMeasure || '');
        setListPrice(
          product.listPrice !== undefined && product.listPrice !== null ? product.listPrice : ''
        );
        setDiscountOnListPricePct(
          product.discountOnListPricePct !== undefined && product.discountOnListPricePct !== null
            ? product.discountOnListPricePct
            : ''
        );
        setNetPrice(
          product.netPrice !== undefined && product.netPrice !== null ? product.netPrice : ''
        );
        setStandardDiscountOnNetPricePct(
          product.standardDiscountOnNetPricePct !== undefined &&
            product.standardDiscountOnNetPricePct !== null
            ? product.standardDiscountOnNetPricePct
            : ''
        );
        setDiscountAtNetPrice(
          product.discountAtNetPrice !== undefined && product.discountAtNetPrice !== null
            ? product.discountAtNetPrice
            : ''
        );
        setUnderWarranty(Boolean(product.underWarranty));
        setServiceReserved(Boolean(product.serviceReserved));
        setSafetyStock(Boolean(product.safetyStock));
        setMoq(
          product.moq !== undefined && product.moq !== null && product.moq !== ''
            ? String(product.moq)
            : ''
        );
        setHsnCode(product.hsnCode || '');
        setPreferredVendor1Id(product.preferredVendor1Id || '');
        setPreferredVendor2Id(product.preferredVendor2Id || '');
        setAbcCategory(product.abcCategory || '');
        setDescription(product.description || '');
        if (product.productServiceId != null) {
          setProductServiceId(String(product.productServiceId));
        }
        if (product.productServiceId != null || product.category) {
          setCategories((prev) => {
            const id = product.productServiceId != null ? String(product.productServiceId) : '';
            const name = product.category || i18n.t('inventory.serviceNumber', { id });
            if (!id) {
              // Legacy: only category name — keep as synthetic option so select can show it
              if (prev.some((c) => c.name === product.category)) return prev;
              return [
                ...prev,
                { id: product.category, productServiceId: null, name: product.category, label: product.category },
              ];
            }
            if (prev.some((c) => String(c.id) === id)) return prev;
            return [...prev, { id, productServiceId: Number(id), name, label: name }];
          });
        }
        if (product.preferredVendor1Id) {
          setPrimaryVendors((prev) => {
            if (prev.some((v) => String(v.id) === String(product.preferredVendor1Id))) return prev;
            const name = product.preferredVendor1 || i18n.t('inventory.vendorNumber', { id: product.preferredVendor1Id });
            return [...prev, { id: String(product.preferredVendor1Id), name, label: name }];
          });
        }
        if (product.preferredVendor2Id) {
          setSecondaryVendors((prev) => {
            if (prev.some((v) => String(v.id) === String(product.preferredVendor2Id))) return prev;
            const name = product.preferredVendor2 || i18n.t('inventory.vendorNumber', { id: product.preferredVendor2Id });
            return [...prev, { id: String(product.preferredVendor2Id), name, label: name }];
          });
        }
        if (product.abcCategory) {
          setAbcOptions((prev) => {
            if (prev.some((o) => o.value === product.abcCategory)) return prev;
            return [
              ...prev,
              { value: product.abcCategory, label: i18n.t('inventory.gradeLabel', { abcCategory: product.abcCategory }) },
            ];
          });
        }
      } catch (err) {
        if (!cancelled) setLoadError(err?.message || i18n.t('inventory.failedToLoadProduct'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [isEditRoute, productId]);

  const onCancel = () => navigate('/IMS/inventory/products');

  const handleCategoryChange = (serviceId) => {
    setProductServiceId(serviceId);
    const selected = categories.find((c) => String(c.id) === String(serviceId));
    setCategory(selected?.name || selected?.label || '');
    if (errors.category) setErrors({ ...errors, category: null });
  };

  /** Recalculate Net Price (and Discount at Net Price) from List Price + Discount %. */
  const applyListPriceDrivenPricing = (nextListPrice, nextDiscountPct, nextStandardDiscountPct) => {
    const nextNetPrice = calculateNetPrice(nextListPrice, nextDiscountPct);
    setNetPrice(nextNetPrice);
    setDiscountAtNetPrice(calculateDiscountAtNetPrice(nextNetPrice, nextStandardDiscountPct));
    if (errors.netPrice || errors.discountAtNetPrice) {
      setErrors((prev) => ({ ...prev, netPrice: null, discountAtNetPrice: null }));
    }
  };

  const handleListPriceChange = (raw) => {
    const nextListPrice = sanitizeDecimalInput(raw);
    setListPrice(nextListPrice);
    if (errors.listPrice) setErrors((prev) => ({ ...prev, listPrice: null }));
    applyListPriceDrivenPricing(nextListPrice, discountOnListPricePct, standardDiscountOnNetPricePct);
  };

  const handleDiscountOnListPricePctChange = (raw) => {
    const nextDiscountPct = sanitizeDecimalInput(raw);
    setDiscountOnListPricePct(nextDiscountPct);
    if (errors.discountOnListPricePct) setErrors((prev) => ({ ...prev, discountOnListPricePct: null }));
    applyListPriceDrivenPricing(listPrice, nextDiscountPct, standardDiscountOnNetPricePct);
  };

  const handleStandardDiscountOnNetPricePctChange = (raw) => {
    const nextStandardDiscountPct = sanitizeDecimalInput(raw);
    setStandardDiscountOnNetPricePct(nextStandardDiscountPct);
    if (errors.standardDiscountOnNetPricePct) {
      setErrors((prev) => ({ ...prev, standardDiscountOnNetPricePct: null }));
    }
    setDiscountAtNetPrice(calculateDiscountAtNetPrice(netPrice, nextStandardDiscountPct));
    if (errors.discountAtNetPrice) setErrors((prev) => ({ ...prev, discountAtNetPrice: null }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};

    if (!name.trim()) newErrors.name = i18n.t('inventory.productNameMandatory');
    if (!partNumber.trim()) newErrors.partNumber = i18n.t('inventory.partNumberMandatory');
    if (!productServiceId && !category) newErrors.category = i18n.t('inventory.categoryMandatory');
    if (!manufacturer.trim()) newErrors.manufacturer = i18n.t('inventory.manufacturerMandatory');
    if (!status) newErrors.status = i18n.t('inventory.statusMandatory');
    if (!metricId) newErrors.unitOfMeasure = i18n.t('inventory.unitOfMeasureMandatory');

    if (listPrice === '' || listPrice === undefined || isNaN(Number(listPrice)) || Number(listPrice) <= 0) {
      newErrors.listPrice = i18n.t('inventory.listPriceMandatoryHint');
    }
    if (discountOnListPricePct === '' || discountOnListPricePct === undefined) {
      newErrors.discountOnListPricePct = i18n.t('common.fieldRequired', {
        field: i18n.t('inventory.discountOnListPricePct'),
      });
    } else if (
      isNaN(Number(discountOnListPricePct)) ||
      Number(discountOnListPricePct) < 0 ||
      Number(discountOnListPricePct) > 100
    ) {
      newErrors.discountOnListPricePct = i18n.t('inventory.discountPercentageInvalid');
    }
    if (netPrice === '' || netPrice === undefined || isNaN(Number(netPrice)) || Number(netPrice) < 0) {
      newErrors.netPrice = i18n.t('inventory.netPriceMandatory');
    }
    if (standardDiscountOnNetPricePct === '' || standardDiscountOnNetPricePct === undefined) {
      newErrors.standardDiscountOnNetPricePct = i18n.t('common.fieldRequired', {
        field: i18n.t('inventory.standardDiscountOnNetPricePct'),
      });
    } else if (
      isNaN(Number(standardDiscountOnNetPricePct)) ||
      Number(standardDiscountOnNetPricePct) < 0 ||
      Number(standardDiscountOnNetPricePct) > 100
    ) {
      newErrors.standardDiscountOnNetPricePct = i18n.t('inventory.discountPercentageInvalid');
    }
    if (
      discountAtNetPrice !== '' &&
      (isNaN(Number(discountAtNetPrice)) || Number(discountAtNetPrice) < 0)
    ) {
      newErrors.discountAtNetPrice = i18n.t('inventory.discountAmountInvalid');
    }
    if (hsnCode && !/^\d{1,8}$/.test(hsnCode)) {
      newErrors.hsnCode = i18n.t('inventory.hsnCodeDigitsOnly');
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setValidationAlert(i18n.t('inventory.fillAllMandatoryFields'));
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    const selectedCategory = categories.find((c) => String(c.id) === String(productServiceId));
    const formPayload = {
      name: name.trim(),
      partNumber: partNumber.trim(),
      category: selectedCategory?.name || category,
      manufacturer: manufacturer.trim(),
      status,
      metricId,
      metricCategory: metricCategory || undefined,
      listPrice: Number(listPrice),
      discountOnListPricePct:
        discountOnListPricePct !== '' ? Number(discountOnListPricePct) : undefined,
      netPrice: Number(netPrice),
      standardDiscountOnNetPricePct:
        standardDiscountOnNetPricePct !== '' ? Number(standardDiscountOnNetPricePct) : undefined,
      initialInputPrice: discountAtNetPrice !== '' ? Number(discountAtNetPrice) : undefined,
      serviceReserved,
      safetyStock,
      underWarranty,
      moq: moq !== '' ? Number(moq) : undefined,
      hsnCode: hsnCode || undefined,
      preferredVendor1Id: preferredVendor1Id || undefined,
      preferredVendor2Id: preferredVendor2Id || undefined,
      abcCategory: abcCategory || undefined,
      description: description.trim() || undefined,
      productServiceId: productServiceId || undefined,
    };

    setSaving(true);
    setValidationAlert(null);
    try {
      const saved = isEditRoute
        ? await updateProduct(productId, formPayload)
        : await createProduct(formPayload);

      const savedId = saved?.id || saved?.productId || productId;
      sessionStorage.setItem(
        TOAST_KEY,
        JSON.stringify({
          message: isEditRoute
            ? i18n.t('inventory.productUpdatedSuccess')
            : i18n.t('inventory.productCreatedSuccess'),
          type: 'success',
        })
      );
      navigate(`/IMS/inventory/products/${savedId}`);
    } catch (err) {
      setValidationAlert(err?.message || i18n.t('inventory.failedToSaveProduct'));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSaving(false);
    }
  };

  if (loading || lookupsLoading) {
    return (
      <AppLayout formLayout="standard">
        <div className="inventory-module">
          <div style={{ padding: '40px', textAlign: 'center' }}>
            <p style={{ color: 'var(--text-grey)', fontSize: '14px' }}>
              {loading ? i18n.t('inventory.loadingProduct') : i18n.t('inventory.loadingFormOptions')}
            </p>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (loadError) {
    return (
      <AppLayout formLayout="standard">
        <div className="inventory-module">
          <div style={{ padding: '40px', textAlign: 'center', backgroundColor: '#ffffff', borderRadius: '16px', border: '1px solid var(--border-color)' }}>
            <h3 style={{ fontSize: '18px', fontWeight: '700', color: 'var(--primary-navy)' }}>{loadError}</h3>
            <button type="button" className="btn-secondary" style={{ marginTop: '16px' }} onClick={() => navigate('/IMS/inventory/products')}>
              <Trans i18nKey="inventory.backToCatalog" />
            </button>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout formLayout="standard">
      <div className="inventory-module">
    <div>
      <div style={{ marginBottom: '20px' }}>
        <PageTitleRow back={<PageBackLink to="/IMS/inventory/products" />}>
          <h1 className="page-title">
            {isEditRoute ? <Trans i18nKey="inventory.editProduct" /> : <Trans i18nKey="formActions.addProduct" />}
          </h1>
        </PageTitleRow>
      </div>

      {lookupsError && (
        <div className="form-warning-alert" style={{ marginBottom: '16px', display: 'flex', gap: '12px', padding: '14px 16px', backgroundColor: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', color: '#92400e', alignItems: 'center' }}>
          <AlertTriangle size={20} style={{ flexShrink: 0 }} />
          <span style={{ fontSize: '14px', fontWeight: '600' }}>
            {i18n.t('inventory.someDropdownOptionsFailed', { lookupsError })}
          </span>
        </div>
      )}

      {validationAlert && (
        <div className="form-warning-alert" style={{ marginBottom: '24px', display: 'flex', gap: '12px', padding: '14px 16px', backgroundColor: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '8px', color: '#991b1b', alignItems: 'center' }}>
          <AlertTriangle size={20} style={{ flexShrink: 0 }} />
          <span style={{ fontSize: '14px', fontWeight: '600' }}>{validationAlert}</span>
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        className="form-container-constrained inv-product-form"
        style={{ backgroundColor: '#ffffff', padding: '20px 24px', borderRadius: '16px', border: '1px solid var(--border-color)', boxShadow: 'var(--shadow-sm)' }}
      >
        {/* Line 1 — Product Name, Part Number, Status */}
        <div className="inv-responsive-grid--3">
          <div className="form-group">
            <label className="form-label">
              <Trans i18nKey="inventory.productName" />
              <span className="form-required">*</span>
            </label>
            <input
              type="text"
              className={`form-input ${errors.name ? 'form-input-error' : ''}`}
              placeholder={i18n.t('inventory.productNamePlaceholder')}
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (errors.name) setErrors({ ...errors, name: null });
              }}
              style={{ borderColor: errors.name ? 'var(--accent-red)' : '' }}
            />
            {errors.name && <span style={{ fontSize: '11px', color: 'var(--accent-red)', marginTop: '2px', display: 'block' }}>{errors.name}</span>}
          </div>

          <div className="form-group">
            <label className="form-label">
              <Trans i18nKey="inventory.partNumber" />
              <span className="form-required">*</span>
            </label>
            <div className="inv-part-suggest" ref={partSuggestRef}>
              <input
                type="text"
                className={`form-input ${errors.partNumber ? 'form-input-error' : ''}`}
                placeholder={i18n.t('inventory.searchOrEnterPartNumber')}
                value={partNumber}
                onChange={(e) => {
                  setPartNumber(e.target.value);
                  if (errors.partNumber) setErrors({ ...errors, partNumber: null });
                }}
                onFocus={() => {
                  if (partSuggestions.length > 0) setPartSuggestOpen(true);
                }}
                autoComplete="off"
                style={{ borderColor: errors.partNumber ? 'var(--accent-red)' : '' }}
              />
              {partSuggestOpen && (
                <div className="inv-part-suggest__list">
                  {partSuggestLoading && (
                    <div className="inv-metric-tree__empty"><Trans i18nKey="inventory.searching" /></div>
                  )}
                  {!partSuggestLoading &&
                    partSuggestions.map((s) => (
                      <button
                        key={`${s.id || s.partNumber}`}
                        type="button"
                        className="inv-part-suggest__item"
                        onClick={() => {
                          setPartNumber(s.partNumber);
                          if (s.name) setName(s.name);
                          if (s.category) {
                            setCategory(s.category);
                          }
                          setPartSuggestOpen(false);
                        }}
                      >
                        <span className="inv-part-suggest__pn">{s.partNumber}</span>
                        {s.name ? <span className="inv-part-suggest__name">{s.name}</span> : null}
                      </button>
                    ))}
                </div>
              )}
            </div>
            {errors.partNumber && <span style={{ fontSize: '11px', color: 'var(--accent-red)', marginTop: '2px', display: 'block' }}>{errors.partNumber}</span>}
          </div>

          <div className="form-group">
            <label className="form-label">
              <Trans i18nKey="status.status" />
              <span className="form-required">*</span>
            </label>
            <FormSelect
              className="form-select"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                if (errors.status) setErrors({ ...errors, status: null });
              }}
              style={{ borderColor: errors.status ? 'var(--accent-red)' : '' }}
              disabled={statusSelectOptions.length === 0}
            >
              <option value="">
                {statusSelectOptions.length === 0 ? i18n.t('inventory.noStatusesAvailable') : i18n.t('common.selectStatus')}
              </option>
              {statusSelectOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {translateProductStatusLabel(opt.value, opt.label)}
                </option>
              ))}
            </FormSelect>
            {status === 'End of Production' && (
              <span style={{ fontSize: '11px', color: '#b45309', marginTop: '2px', display: 'block' }}>
                <Trans i18nKey="inventory.endOfProductionPurchaseNote" />
              </span>
            )}
            {errors.status && <span style={{ fontSize: '11px', color: 'var(--accent-red)', marginTop: '2px', display: 'block' }}>{errors.status}</span>}
          </div>
        </div>

        {/* Line 2 — Category, Manufacturer, Unit of Measure */}
        <div className="inv-responsive-grid--3 inv-product-dropdown-row">
          <div className="form-group">
            <label className="form-label">
              <Trans i18nKey="common.category" />
              <span className="form-required">*</span>
            </label>
            <FormSelect
              className="form-select inv-form-dropdown-control"
              value={productServiceId}
              onChange={(e) => handleCategoryChange(e.target.value)}
              style={{ borderColor: errors.category ? 'var(--accent-red)' : '' }}
              disabled={categories.length === 0}
            >
              <option value="">
                {categories.length === 0 ? i18n.t('inventory.noCategoriesAvailable') : i18n.t('onboarding.selectCategory')}
              </option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name || cat.label}
                </option>
              ))}
            </FormSelect>
            {errors.category && <span style={{ fontSize: '11px', color: 'var(--accent-red)', marginTop: '2px', display: 'block' }}>{errors.category}</span>}
          </div>

          <div className="form-group">
            <label className="form-label">
              <Trans i18nKey="crm.manufacturer" />
              <span className="form-required">*</span>
            </label>
            <input
              type="text"
              className={`form-input ${errors.manufacturer ? 'form-input-error' : ''}`}
              placeholder={i18n.t('inventory.manufacturerPlaceholder')}
              value={manufacturer}
              onChange={(e) => {
                setManufacturer(e.target.value);
                if (errors.manufacturer) setErrors({ ...errors, manufacturer: null });
              }}
              style={{ borderColor: errors.manufacturer ? 'var(--accent-red)' : '' }}
            />
            {errors.manufacturer && <span style={{ fontSize: '11px', color: 'var(--accent-red)', marginTop: '2px', display: 'block' }}>{errors.manufacturer}</span>}
          </div>

          <div className="form-group inv-uom-field">
            <label className="form-label">
              <Trans i18nKey="productMetrics.unitOfMeasure" />
              <span className="form-required">*</span>
            </label>
            <MetricTreeSelect
              categories={metricCategories}
              value={metricId}
              categoryValue={metricCategory}
              displayLabel={metricLabel}
              error={Boolean(errors.unitOfMeasure)}
              loadingCategories={lookupsLoading}
              onChange={({ metricId: id, metricCategory: cat, label }) => {
                setMetricId(id);
                setMetricCategory(cat);
                setMetricLabel(label || '');
                if (errors.unitOfMeasure) setErrors({ ...errors, unitOfMeasure: null });
              }}
            />
            {errors.unitOfMeasure && <span style={{ fontSize: '11px', color: 'var(--accent-red)', marginTop: '2px', display: 'block' }}>{errors.unitOfMeasure}</span>}
          </div>
        </div>

        {/* Line 3 — List Price, Discount on List Price, Net Price */}
        <div className="inv-responsive-grid--3">
          <div className="form-group">
            <label className="form-label">
              <Trans i18nKey="inventory.listPrice" />
              <span className="form-required">*</span>
            </label>
            <input
              type="text"
              inputMode="decimal"
              className={`form-input no-spinner ${errors.listPrice ? 'form-input-error' : ''}`}
              placeholder={i18n.t('inventory.pricePlaceholder15000')}
              value={listPrice}
              onChange={(e) => handleListPriceChange(e.target.value)}
              style={{ borderColor: errors.listPrice ? 'var(--accent-red)' : '' }}
            />
            {errors.listPrice && <span style={{ fontSize: '11px', color: 'var(--accent-red)', marginTop: '2px', display: 'block' }}>{errors.listPrice}</span>}
          </div>

          <div className="form-group">
            <label className="form-label">
              <Trans i18nKey="inventory.discountOnListPricePct" />
              <span className="form-required">*</span>
            </label>
            <input
              type="text"
              inputMode="decimal"
              className={`form-input no-spinner ${errors.discountOnListPricePct ? 'form-input-error' : ''}`}
              placeholder={i18n.t('inventory.percentagePlaceholder')}
              value={discountOnListPricePct}
              onChange={(e) => handleDiscountOnListPricePctChange(e.target.value)}
              style={{ borderColor: errors.discountOnListPricePct ? 'var(--accent-red)' : '' }}
            />
            {errors.discountOnListPricePct && <span style={{ fontSize: '11px', color: 'var(--accent-red)', marginTop: '2px', display: 'block' }}>{errors.discountOnListPricePct}</span>}
          </div>

          <div className="form-group">
            <label className="form-label">
              <Trans i18nKey="inventory.netPrice" />
              <span className="form-required">*</span>
            </label>
            <input
              type="text"
              inputMode="decimal"
              className={`form-input no-spinner ${errors.netPrice ? 'form-input-error' : ''}`}
              placeholder={i18n.t('inventory.pricePlaceholder18500')}
              value={netPrice}
              readOnly
              style={{ borderColor: errors.netPrice ? 'var(--accent-red)' : '', backgroundColor: '#f8fafc' }}
            />
            {errors.netPrice && <span style={{ fontSize: '11px', color: 'var(--accent-red)', marginTop: '2px', display: 'block' }}>{errors.netPrice}</span>}
          </div>
        </div>

        {/* Line 4 — Standard Discount on Net Price, Initial Input Price, HSN Code */}
        <div className="inv-responsive-grid--3">
          <div className="form-group">
            <label className="form-label">
              <Trans i18nKey="inventory.standardDiscountOnNetPricePct" />
              <span className="form-required">*</span>
            </label>
            <input
              type="text"
              inputMode="decimal"
              className={`form-input no-spinner ${errors.standardDiscountOnNetPricePct ? 'form-input-error' : ''}`}
              placeholder={i18n.t('inventory.percentagePlaceholder')}
              value={standardDiscountOnNetPricePct}
              onChange={(e) => handleStandardDiscountOnNetPricePctChange(e.target.value)}
              style={{ borderColor: errors.standardDiscountOnNetPricePct ? 'var(--accent-red)' : '' }}
            />
            {errors.standardDiscountOnNetPricePct && <span style={{ fontSize: '11px', color: 'var(--accent-red)', marginTop: '2px', display: 'block' }}>{errors.standardDiscountOnNetPricePct}</span>}
          </div>

          <div className="form-group">
            <label className="form-label"><Trans i18nKey="inventory.initialInputPrice" /></label>
            <input
              type="text"
              inputMode="decimal"
              className={`form-input no-spinner ${errors.discountAtNetPrice ? 'form-input-error' : ''}`}
              placeholder={i18n.t('inventory.pricePlaceholder18500')}
              value={discountAtNetPrice}
              readOnly
              style={{ borderColor: errors.discountAtNetPrice ? 'var(--accent-red)' : '', backgroundColor: '#f8fafc' }}
            />
            {errors.discountAtNetPrice && <span style={{ fontSize: '11px', color: 'var(--accent-red)', marginTop: '2px', display: 'block' }}>{errors.discountAtNetPrice}</span>}
          </div>

          <div className="form-group">
            <label className="form-label"><Trans i18nKey="inventory.hsnCode" /></label>
            <input
              type="text"
              inputMode="numeric"
              className={`form-input ${errors.hsnCode ? 'form-input-error' : ''}`}
              placeholder={i18n.t('inventory.hsnCodePlaceholder')}
              value={hsnCode}
              maxLength={8}
              onChange={(e) => {
                setHsnCode(sanitizeHsnInput(e.target.value));
                if (errors.hsnCode) setErrors({ ...errors, hsnCode: null });
              }}
              style={{ borderColor: errors.hsnCode ? 'var(--accent-red)' : '' }}
            />
            {errors.hsnCode && <span style={{ fontSize: '11px', color: 'var(--accent-red)', marginTop: '2px', display: 'block' }}>{errors.hsnCode}</span>}
          </div>
        </div>

        {/* Line 5 — Service Safety Stock, Under Warranty, Sales Safety Stock, MOQ */}
        <div className="inv-responsive-grid--4">
          <div className="form-group">
            <label className="form-label"><Trans i18nKey="inventory.safetyStock" /></label>
            <label htmlFor="product-safety-stock" className="inv-checkbox-field">
              <input
                id="product-safety-stock"
                type="checkbox"
                checked={safetyStock}
                onChange={(e) => setSafetyStock(e.target.checked)}
                style={{ width: '16px', height: '16px', accentColor: '#152273', cursor: 'pointer' }}
              />
              <span>{i18n.t('common.yes')}</span>
            </label>
          </div>
          <div className="form-group">
            <label className="form-label"><Trans i18nKey="inventory.serviceReserved" /></label>
            <label htmlFor="product-service-reserved" className="inv-checkbox-field">
              <input
                id="product-service-reserved"
                type="checkbox"
                checked={serviceReserved}
                onChange={(e) => setServiceReserved(e.target.checked)}
                style={{ width: '16px', height: '16px', accentColor: '#152273', cursor: 'pointer' }}
              />
              <span>{i18n.t('common.yes')}</span>
            </label>
          </div>
          <div className="form-group">
            <label className="form-label"><Trans i18nKey="inventory.underWarranty" /></label>
            <label htmlFor="product-under-warranty" className="inv-checkbox-field">
              <input
                id="product-under-warranty"
                type="checkbox"
                checked={underWarranty}
                onChange={(e) => setUnderWarranty(e.target.checked)}
                style={{ width: '16px', height: '16px', accentColor: '#152273', cursor: 'pointer' }}
              />
              <span>{i18n.t('common.yes')}</span>
            </label>
          </div>
          
          <div className="form-group">
            <label className="form-label"><Trans i18nKey="inventory.moqMinOrderQty" /></label>
            <input
              type="text"
              inputMode="numeric"
              className="form-input no-spinner"
              placeholder={i18n.t('inventory.reorderQtyPlaceholder')}
              value={moq}
              onChange={(e) => setMoq(sanitizeIntegerInput(e.target.value))}
            />
          </div>
        </div>

        {/* Line 6 — Preferred Vendor 1, Preferred Vendor 2, ABC Analysis Category */}
        <div className="inv-responsive-grid--3">
          <div className="form-group">
            <label className="form-label"><Trans i18nKey="inventory.preferredVendor1" /></label>
            <FormSelect
              className="form-select"
              value={preferredVendor1Id}
              onChange={(e) => setPreferredVendor1Id(e.target.value)}
            >
              <option value="">
                {primaryVendors.length === 0 ? i18n.t('inventory.noVendorsAvailable') : i18n.t('inventory.selectVendor')}
              </option>
              {primaryVendors.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.label || v.name}
                </option>
              ))}
            </FormSelect>
          </div>

          <div className="form-group">
            <label className="form-label"><Trans i18nKey="inventory.preferredVendor2" /></label>
            <FormSelect
              className="form-select"
              value={preferredVendor2Id}
              onChange={(e) => setPreferredVendor2Id(e.target.value)}
            >
              <option value="">
                {secondaryVendors.length === 0 ? i18n.t('inventory.noVendorsAvailable') : i18n.t('inventory.selectVendor')}
              </option>
              {secondaryVendors.map((v) => (
                <option key={`v2-${v.id}`} value={v.id}>
                  {v.label || v.name}
                </option>
              ))}
            </FormSelect>
          </div>

          <div className="form-group">
            <label className="form-label"><Trans i18nKey="inventory.abcAnalysisCategory" /></label>
            <FormSelect
              className="form-select"
              value={abcCategory}
              onChange={(e) => setAbcCategory(e.target.value)}
            >
              <option value=""><Trans i18nKey="inventory.noneNotClassified" /></option>
              {abcOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </FormSelect>
          </div>
        </div>

        {/* Line 7 — Description */}
        <div className="form-group">
          <label className="form-label"><Trans i18nKey="common.description" /></label>
          <textarea
            className="form-input"
            style={{ resize: 'vertical' }}
            placeholder={i18n.t('inventory.productDescriptionPlaceholder')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        <div className="form-buttons-right inv-form-footer" style={{ borderTop: '1px solid var(--border-color)', paddingTop: '16px', marginTop: '4px' }}>
          <button
            type="button"
            className="btn-secondary"
            onClick={onCancel}
            disabled={saving}
            style={{ height: '40px', borderRadius: '8px', padding: '0 20px', fontWeight: '600', fontSize: '13px' }}
          >
            <Trans i18nKey="formActions.cancel" />
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={saving}
            style={{ height: '40px', borderRadius: '8px', padding: '0 20px', fontWeight: '600', fontSize: '13px', opacity: saving ? 0.7 : 1 }}
          >
            {saving ? i18n.t('inventory.savingEllipsis') : i18n.t('inventory.saveProduct')}
          </button>
        </div>
      </form>
    </div>
      </div>
    </AppLayout>
  );
}
